import React, { useEffect, useState, Component } from 'react';
import { CalculatorIcon, DownloadIcon, SendIcon, RefreshCwIcon, CheckCircleIcon } from 'lucide-react';
import { calculateQuotation, QuotationData, QuotationResult } from '../utils/quotationLogic';
import { generateQuotationPDF } from '../utils/pdfGenerator';
export function QuotationCalculator() {
  const [formData, setFormData] = useState<QuotationData>({
    contribuyente: '',
    regimen: '',
    activos: '',
    contabilidadCompleta: '',
    facturacion: '',
    tipoNegocio: '',
    certificador: '',
    nombre: '',
    empresa: '',
    whatsapp: '',
    correo: ''
  });
  const [result, setResult] = useState<QuotationResult | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  // Auto-select contabilidad completa when conditions are met
  useEffect(() => {
    const needsFullAccounting = (formData.regimen === 'iva-isr-5-7' || formData.regimen === 'iva-isr-25-iso') && formData.activos === 'mayor-25k';
    if (needsFullAccounting) {
      setFormData(prev => ({
        ...prev,
        contabilidadCompleta: 'si'
      }));
    }
  }, [formData.regimen, formData.activos]);
  const handleChange = (field: keyof QuotationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleCalculate = () => {
    if (!formData.contribuyente || !formData.regimen || !formData.activos || !formData.facturacion || !formData.tipoNegocio || !formData.certificador) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }
    const calculationResult = calculateQuotation(formData);
    setResult(calculationResult);
  };
  const handleReset = () => {
    setFormData({
      contribuyente: '',
      regimen: '',
      activos: '',
      contabilidadCompleta: '',
      facturacion: '',
      tipoNegocio: '',
      certificador: '',
      nombre: '',
      empresa: '',
      whatsapp: '',
      correo: ''
    });
    setResult(null);
    setShowContactForm(false);
  };
  const handleDownloadPDF = () => {
    if (!result) return;
    const pdfData = {
      nombre: formData.nombre,
      empresa: formData.empresa,
      whatsapp: formData.whatsapp,
      correo: formData.correo,
      breakdown: result.breakdown,
      total: result.total,
      warnings: result.warnings,
      date: new Date().toLocaleDateString('es-GT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
    generateQuotationPDF(pdfData);
  };
  const handleSendQuotation = () => {
    if (!result) return;
    // Build WhatsApp message
    let message = '🧾 *COTIZACIÓN KONTAXES*\n\n';
    if (formData.nombre || formData.empresa) {
      message += '*DATOS DEL CLIENTE:*\n';
      if (formData.nombre) message += `Nombre: ${formData.nombre}\n`;
      if (formData.empresa) message += `Empresa: ${formData.empresa}\n`;
      if (formData.whatsapp) message += `WhatsApp: ${formData.whatsapp}\n`;
      if (formData.correo) message += `Correo: ${formData.correo}\n`;
      message += '\n';
    }
    message += '*SERVICIOS SELECCIONADOS:*\n';
    result.breakdown.forEach(item => {
      message += `• ${item.item}\n`;
      if (item.note) message += `  _${item.note}_\n`;
    });
    message += `\n*TOTAL MENSUAL APROXIMADO: Q${result.total}*\n\n`;
    if (result.warnings.length > 0) {
      message += '*NOTAS IMPORTANTES:*\n';
      result.warnings.forEach(warning => {
        message += `⚠️ ${warning}\n`;
      });
      message += '\n';
    }
    message += '📞 Quiero más información para formalizar los servicios cotizados.';
    const whatsappUrl = `https://wa.me/50236387717?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };
  const showContabilidadSection = formData.activos === 'mayor-25k';
  const isContabilidadRequired = (formData.regimen === 'iva-isr-5-7' || formData.regimen === 'iva-isr-25-iso') && formData.activos === 'mayor-25k';
  const OptionButton = ({
    selected,
    onClick,
    children,
    disabled = false
  }: {
    selected: boolean;
    onClick: () => void;
    children: React.ReactNode;
    disabled?: boolean;
  }) => <button onClick={onClick} disabled={disabled} className={`relative p-5 rounded-xl border-2 transition-all duration-200 text-left ${selected ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30 shadow-lg scale-[1.02]' : 'border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 bg-white dark:bg-gray-800'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`} data-id="element-176">
      {selected && <div className="absolute top-3 right-3" data-id="element-177">
          <CheckCircleIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-178" />
        </div>}
      {children}
    </button>;
  return <section id="cotizador" className="py-20 bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-900 dark:to-gray-800" data-id="element-179">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-180">
        <div className="text-center mb-12" data-id="element-181">
          <div className="inline-block mb-4" data-id="element-182">
            <span className="px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-semibold" data-id="element-183">
              💰 Cotización Instantánea
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-184">
            Cotizador de Servicios
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400" data-id="element-185">
            Calcula el costo aproximado de nuestros servicios contables
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden" data-id="element-186">
          <div className="p-8 md:p-12" data-id="element-187">
            {/* Tipo de contribuyente */}
            <div className="mb-8" data-id="element-188">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-189">
                1. Tipo de contribuyente
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-190">
                <OptionButton selected={formData.contribuyente === 'individual'} onClick={() => handleChange('contribuyente', 'individual')} data-id="element-191">
                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-192">
                    Empresa o persona individual
                  </div>
                </OptionButton>
                <OptionButton selected={formData.contribuyente === 'sociedad'} onClick={() => handleChange('contribuyente', 'sociedad')} data-id="element-193">
                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-194">
                    Sociedad Anónima
                  </div>
                </OptionButton>
              </div>
            </div>

            {/* Régimen de impuestos */}
            <div className="mb-8" data-id="element-195">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-196">
                2. Régimen de impuestos
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-197">
                {[{
                value: 'pequeño',
                label: 'IVA Pequeño contribuyente 5%'
              }, {
                value: 'iva-isr-5-7',
                label: 'IVA 12% + ISR 5-7%'
              }, {
                value: 'iva-isr-25-iso',
                label: 'IVA 12% + ISR 25% + ISO'
              }, {
                value: 'no-se',
                label: 'No sé cuál me conviene'
              }].map(option => <OptionButton key={option.value} selected={formData.regimen === option.value} onClick={() => handleChange('regimen', option.value)} data-id="element-198">
                    <div className="font-semibold text-gray-900 dark:text-white" data-id="element-199">
                      {option.label}
                    </div>
                  </OptionButton>)}
              </div>
            </div>

            {/* Monto de activos */}
            <div className="mb-8" data-id="element-200">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-201">
                3. Monto de activos
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-202">
                <OptionButton selected={formData.activos === 'menor-25k'} onClick={() => handleChange('activos', 'menor-25k')} data-id="element-203">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-204">
                    De Q0 a Q25,000
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-205">
                    No es necesario llevar contabilidad completa
                  </div>
                </OptionButton>
                <OptionButton selected={formData.activos === 'mayor-25k'} onClick={() => handleChange('activos', 'mayor-25k')} data-id="element-206">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-207">
                    Mayor a Q25,000
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-208">
                    Puede requerir contabilidad completa
                  </div>
                </OptionButton>
              </div>
            </div>

            {/* Contabilidad completa */}
            {showContabilidadSection && <div className="mb-8 p-6 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border-2 border-purple-200 dark:border-purple-800" data-id="element-209">
                <label className="block text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-210">
                  4. Contabilidad completa
                  {isContabilidadRequired && <span className="ml-2 text-red-600 dark:text-red-400 text-base" data-id="element-211">
                      (Obligatorio por ley)
                    </span>}
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-212">
                  Se utilizará el sistema Odoo para gestión contable completa
                </p>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-213">
                  <OptionButton selected={formData.contabilidadCompleta === 'si'} onClick={() => !isContabilidadRequired && handleChange('contabilidadCompleta', 'si')} disabled={isContabilidadRequired} data-id="element-214">
                    <div className="font-semibold text-gray-900 dark:text-white" data-id="element-215">
                      Sí, con Odoo
                    </div>
                  </OptionButton>
                  <OptionButton selected={formData.contabilidadCompleta === 'no'} onClick={() => !isContabilidadRequired && handleChange('contabilidadCompleta', 'no')} disabled={isContabilidadRequired} data-id="element-216">
                    <div className="font-semibold text-gray-900 dark:text-white" data-id="element-217">
                      No
                    </div>
                  </OptionButton>
                </div>
              </div>}

            {/* Cantidad de facturación */}
            <div className="mb-8" data-id="element-218">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-219">
                {showContabilidadSection ? '5' : '4'}. Cantidad de facturación
                mensual
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-220">
                {[{
                value: '0-10',
                label: 'De 0 a 10 al mes'
              }, {
                value: '11-50',
                label: 'De 11 a 50 al mes'
              }, {
                value: '51-100',
                label: 'De 51 a 100 al mes'
              }, {
                value: 'mas-100',
                label: 'Más de 100 al mes'
              }].map(option => <OptionButton key={option.value} selected={formData.facturacion === option.value} onClick={() => handleChange('facturacion', option.value)} data-id="element-221">
                    <div className="font-semibold text-gray-900 dark:text-white" data-id="element-222">
                      {option.label}
                    </div>
                  </OptionButton>)}
              </div>
            </div>

            {/* Tipo de negocio */}
            <div className="mb-8" data-id="element-223">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-224">
                {showContabilidadSection ? '6' : '5'}. Tipo de negocio
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-225">
                <OptionButton selected={formData.tipoNegocio === 'servicios'} onClick={() => handleChange('tipoNegocio', 'servicios')} data-id="element-226">
                  <div className="font-semibold text-gray-900 dark:text-white" data-id="element-227">
                    Servicios técnicos, profesionales o en general
                  </div>
                </OptionButton>
                <OptionButton selected={formData.tipoNegocio === 'compra-venta'} onClick={() => handleChange('tipoNegocio', 'compra-venta')} data-id="element-228">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-229">
                    Compra-venta de bienes
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-230">
                    Incluye módulo de inventarios
                  </div>
                </OptionButton>
              </div>
            </div>

            {/* Certificador */}
            <div className="mb-8" data-id="element-231">
              <label className="block text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-232">
                {showContabilidadSection ? '7' : '6'}. ¿Desea implementar un
                certificador?
              </label>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-233">
                <OptionButton selected={formData.certificador === 'si'} onClick={() => handleChange('certificador', 'si')} data-id="element-234">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-235">
                    Sí
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-236">
                    Costo según cantidad de facturas anuales
                  </div>
                </OptionButton>
                <OptionButton selected={formData.certificador === 'no'} onClick={() => handleChange('certificador', 'no')} data-id="element-237">
                  <div className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-238">
                    No
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-239">
                    Se puede implementar después
                  </div>
                </OptionButton>
              </div>
            </div>

            {/* Contact Form Toggle */}
            <div className="mb-6" data-id="element-240">
              <button onClick={() => setShowContactForm(!showContactForm)} className="flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold transition-colors" data-id="element-241">
                <span className="mr-2" data-id="element-242">{showContactForm ? '▼' : '▶'}</span>
                Datos de contacto (opcional para recibir cotización)
              </button>
            </div>

            {/* Contact Form */}
            {showContactForm && <div className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700" data-id="element-243">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-244">
                  <input type="text" placeholder="Nombre completo" value={formData.nombre} onChange={e => handleChange('nombre', e.target.value)} className="px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 transition-all" data-id="element-245" />
                  <input type="text" placeholder="Nombre de empresa" value={formData.empresa} onChange={e => handleChange('empresa', e.target.value)} className="px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 transition-all" data-id="element-246" />
                  <input type="text" placeholder="WhatsApp (+502 1234-5678)" value={formData.whatsapp} onChange={e => handleChange('whatsapp', e.target.value)} className="px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 transition-all" data-id="element-247" />
                  <input type="email" placeholder="Correo electrónico" value={formData.correo} onChange={e => handleChange('correo', e.target.value)} className="px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 transition-all" data-id="element-248" />
                </div>
              </div>}

            {/* Calculate Button */}
            <button onClick={handleCalculate} className="w-full py-5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold text-lg rounded-xl transition-all flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-[1.02]" data-id="element-249">
              <CalculatorIcon size={24} data-id="element-250" />
              <span data-id="element-251">Calcular Cotización</span>
            </button>

            {/* Results */}
            {result && <div className="mt-8 p-8 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 rounded-2xl border-2 border-purple-200 dark:border-purple-800" data-id="element-252">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center" data-id="element-253">
                  <CheckCircleIcon className="w-8 h-8 text-green-500 mr-3" data-id="element-254" />
                  Resumen de Cotización
                </h3>

                <div className="space-y-3 mb-6" data-id="element-255">
                  {result.breakdown.map((item, index) => <div key={index} className="flex justify-between items-start p-4 bg-white dark:bg-gray-900 rounded-lg" data-id="element-256">
                      <div className="flex-1" data-id="element-257">
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-258">
                          {item.item}
                        </span>
                        {item.note && <p className="text-sm text-gray-600 dark:text-gray-400 italic mt-1" data-id="element-259">
                            {item.note}
                          </p>}
                      </div>
                    </div>)}
                </div>

                {result.warnings.length > 0 && <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded-lg" data-id="element-260">
                    {result.warnings.map((warning, index) => <p key={index} className="text-sm text-yellow-800 dark:text-yellow-200 mb-2 last:mb-0" data-id="element-261">
                        ⚠️ {warning}
                      </p>)}
                  </div>}

                <div className="pt-6 border-t-2 border-purple-300 dark:border-purple-700 mb-6" data-id="element-262">
                  <div className="flex justify-between items-center p-6 bg-purple-100 dark:bg-purple-900/40 rounded-xl" data-id="element-263">
                    <span className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-264">
                      Total Mensual Aproximado:
                    </span>
                    <span className="text-4xl font-bold text-purple-600 dark:text-purple-400" data-id="element-265">
                      Q{result.total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6" data-id="element-266">
                  <button onClick={handleDownloadPDF} className="py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105" data-id="element-267">
                    <DownloadIcon size={20} data-id="element-268" />
                    <span data-id="element-269">Descargar PDF</span>
                  </button>
                  <button onClick={handleSendQuotation} className="py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105" data-id="element-270">
                    <SendIcon size={20} data-id="element-271" />
                    <span data-id="element-272">Enviar WhatsApp</span>
                  </button>
                  <button onClick={handleReset} className="py-4 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105" data-id="element-273">
                    <RefreshCwIcon size={20} data-id="element-274" />
                    <span data-id="element-275">Nueva Cotización</span>
                  </button>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-lg" data-id="element-276">
                  <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed" data-id="element-277">
                    <strong data-id="element-278">IMPORTANTE:</strong> Esta NO es una cotización
                    formal, para mayor información contacta a un asesor. Al
                    momento de formalizar el servicio, se enviará la propuesta
                    de servicios, la cotización formal, y el contrato de
                    servicios profesionales.
                  </p>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}